import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { UndoActionResponse, UndoState } from '@common/api';
import { dashboardsApi, sheetsApi } from '@data/rest/api';
import { undoScopeId } from '@base/authentication/SignInActions';
import { useI18nContext } from '@i18n/i18n-react';
import { handleApiErrorMessage } from '@util/message';

// A screen that owns a server-side undo stack: a standalone sheet, or a whole dashboard (one stack
// shared across its tiles).
export type UndoTarget = { kind: 'sheet' | 'dashboard'; id: number };

// The stack key an edit is tagged with (sent as `screenKey` on setData). The backend keys each stack
// on (user, undoScopeId, screenKey).
export const undoScreenKey = ({ kind, id }: UndoTarget): string => `${kind}_${id}`;

export type UndoController = {
    // The stack key this screen's edits are tagged with on setData (see undoScreenKey).
    screenKey: string;
    // Latest undo/redo availability, or null until the first response.
    undoState: UndoState | null;
    // Record the UndoState an edit's setData response carried.
    recordState: (state: UndoState) => void;
    undo: () => void;
    redo: () => void;
    // Fetch the current state (on mount / target switch).
    refreshState: () => void;
};

type TargetActions = {
    undo: () => Promise<UndoActionResponse>;
    redo: () => Promise<UndoActionResponse>;
    getState: () => Promise<UndoActionResponse>;
};

const targetActions = ({ kind, id }: UndoTarget): TargetActions => {
    const request = { undoScopeId };
    return kind === 'sheet'
        ? {
              undo: () => sheetsApi.undo(id, request),
              redo: () => sheetsApi.redo(id, request),
              getState: () => sheetsApi.getUndoState(id, request),
          }
        : {
              undo: () => dashboardsApi.undo(id, request),
              redo: () => dashboardsApi.redo(id, request),
              getState: () => dashboardsApi.getUndoState(id, request),
          };
};

/**
 * Drives one screen's undo/redo stack. Undo/redo run server-side (v1 keeps no client history); the
 * affected sheet re-renders via the `sheet-data` SSE, which the backend broadcasts to everyone EXCEPT
 * the caller — so `onApplied` runs after a successful undo/redo to refresh the caller's own grid. Also
 * toasts the transient signals a new UndoState carries (`undoUnavailable` reason / `stale` reset).
 */
export const useUndo = (target: UndoTarget, onApplied?: () => void): UndoController => {
    const { LL } = useI18nContext();
    const [undoState, setUndoState] = useState<UndoState | null>(null);
    const { kind, id } = target;
    const screenKey = undoScreenKey({ kind, id });
    const actions = useMemo(() => targetActions({ kind, id }), [kind, id]);

    // One undo/redo/state request in flight at a time; extra triggers are dropped. The backend also
    // single-flights and 409s overlaps, so the worst case is a no-op.
    const inFlight = useRef(false);
    const run = useCallback(
        async (action: 'undo' | 'redo' | 'state') => {
            if (inFlight.current) return;
            inFlight.current = true;
            try {
                const call = action === 'undo' ? actions.undo : action === 'redo' ? actions.redo : actions.getState;
                const response = await call();
                setUndoState(response.undoState ?? null);
                if (action !== 'state') onApplied?.();
            } catch (e) {
                if (e instanceof Response && e.status === 409) return; // another action mid-flight
                await handleApiErrorMessage(e);
            } finally {
                inFlight.current = false;
            }
        },
        [actions, onApplied]
    );

    const undo = useCallback(() => run('undo'), [run]);
    const redo = useCallback(() => run('redo'), [run]);
    const refreshState = useCallback(() => run('state'), [run]);

    // Toast the transient signals a new UndoState carries. They only arrive from a user's edit/undo/redo
    // (a plain state refresh never carries them), so no gating is needed. Keyed on undoState alone — a
    // locale switch shouldn't re-fire a past toast, and LL is read at fire time so it's already current.
    useEffect(() => {
        if (!undoState) return;
        if (undoState.undoUnavailable) {
            const reasons: Record<string, () => string> = {
                not_supported: LL.UNDO_UNAVAILABLE_NOT_SUPPORTED,
                too_large: LL.UNDO_UNAVAILABLE_TOO_LARGE,
                no_inverse: LL.UNDO_UNAVAILABLE_NO_INVERSE,
                no_longer_allowed: LL.UNDO_UNAVAILABLE_NO_LONGER_ALLOWED,
            };
            toast((reasons[undoState.undoUnavailable] ?? LL.UNDO_UNAVAILABLE_GENERIC)());
        } else if (undoState.stale) {
            toast(LL.UNDO_HISTORY_RESET());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [undoState]);

    return useMemo(
        () => ({ screenKey, undoState, recordState: setUndoState, undo, redo, refreshState }),
        [screenKey, undoState, undo, redo, refreshState]
    );
};
